import { useMemo, useState } from "react";
import type { ReactElement, FormEvent } from "react";
import { Link } from "react-router-dom";

import { apiClient } from "../api/client";

/**
 * Cycle 8 STORY-704 + Cycle 9 STORY-805: Request Demo CTA + form.
 *
 * Phase 2 (Cycle 9 STORY-805): submits to /api/leads endpoint.
 * mailto: kept as graceful fallback if API unreachable.
 *
 * Captures: name, email, company, role, products of interest, message.
 * Validates required fields client-side; honeypot field anti-spam.
 */

const PRODUCTS_OPTIONS = [
  { slug: "shift-brief-safety", label: "shift-brief — Safety / HSE инструктажи" },
  { slug: "instruct-me", label: "instruct-me — обучение охране труда" },
  { slug: "core-logic", label: "core-logic — мониторинг гранулометрии" },
  { slug: "smart-pallet", label: "smart-pallet — оптимизация паллетирования" },
  { slug: "palletix", label: "palletix — 3D визуализация паллет" },
  { slug: "pallet-planner", label: "pallet-planner — планирование отгрузок" },
  { slug: "sales-planner", label: "sales-planner — прогнозы продаж" },
  { slug: "sales-analysis", label: "sales-analysis — ML batch-аналитика" },
  { slug: "sales-platinum", label: "sales-platinum — премиум sales analytics" },
  { slug: "green-safety", label: "green-safety — eco/sustainability" },
  { slug: "crew-processing", label: "crew-processing — orchestration" },
  { slug: "application-system", label: "application-system — agent platform" },
];

const TARGET_EMAIL = "sales@it-navigator.online";

interface FormData {
  name: string;
  email: string;
  company: string;
  role: string;
  phone: string;
  selectedProducts: string[];
  message: string;
}

const INITIAL: FormData = {
  name: "",
  email: "",
  company: "",
  role: "",
  phone: "",
  selectedProducts: [],
  message: "",
};

export function RequestDemoPage(): ReactElement {
  const [data, setData] = useState<FormData>(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  // Cycle 9 STORY-805: track submission state + API errors
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Honeypot field — should remain empty; bots fill all fields
  const [honeypot, setHoneypot] = useState("");

  function update<K extends keyof FormData>(key: K, value: FormData[K]): void {
    setData((d) => ({ ...d, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function toggleProduct(slug: string): void {
    setData((d) => ({
      ...d,
      selectedProducts: d.selectedProducts.includes(slug)
        ? d.selectedProducts.filter((s) => s !== slug)
        : [...d.selectedProducts, slug],
    }));
  }

  function validate(): boolean {
    const newErrors: typeof errors = {};
    if (!data.name.trim()) newErrors.name = "Укажите ваше имя";
    if (!data.email.trim()) newErrors.email = "Укажите email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      newErrors.email = "Некорректный email";
    if (!data.company.trim()) newErrors.company = "Укажите компанию";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const mailtoUrl = useMemo(() => {
    if (!validate || !data.name) return "#";
    const productsText = data.selectedProducts.length
      ? data.selectedProducts
          .map((s) => PRODUCTS_OPTIONS.find((p) => p.slug === s)?.label || s)
          .join("\n  - ")
      : "(не указано)";
    const body = [
      `Здравствуйте!`,
      ``,
      `Прошу запланировать демонстрацию продуктов IT-Navigate.`,
      ``,
      `Имя: ${data.name}`,
      `Компания: ${data.company}`,
      `Роль: ${data.role || "(не указана)"}`,
      `Email: ${data.email}`,
      ``,
      `Интересующие продукты:`,
      `  - ${productsText}`,
      ``,
      `Дополнительная информация:`,
      `${data.message || "(не указана)"}`,
      ``,
      `--`,
      `Отправлено через portal.it-navigator.online`,
    ].join("\n");
    const subject = `Запрос демонстрации IT-Navigate — ${data.company}`;
    return `mailto:${TARGET_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [data]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      // Cycle 9 STORY-805 + Cycle 17 STORY-1702: phone field added
      await apiClient.post("/api/leads", {
        name: data.name,
        email: data.email,
        company: data.company,
        role: data.role || null,
        phone: data.phone || null,
        products_of_interest: data.selectedProducts,
        message: data.message || null,
        honeypot: honeypot || null,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      // Graceful fallback: open mail client + show fallback notice
      const errMsg =
        err && typeof err === "object" && "response" in err
          ? `${(err as { response?: { status?: number } }).response?.status ?? "error"}`
          : "network";
      console.error("[/api/leads] failed:", errMsg, err);
      // Open mailto: as fallback so user doesn't lose their input
      window.location.href = mailtoUrl;
      setSubmitError(
        "API временно недоступен. Открыли почтовый клиент с предзаполненной формой как резервный вариант.",
      );
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <section className="request-demo request-demo--success">
        <div className="request-demo__success-icon" aria-hidden="true">
          ✉
        </div>
        <h1>Спасибо, {data.name.split(" ")[0]}!</h1>
        <p>
          Запрос отправлен в почтовый клиент. Если письмо не открылось,
          напишите нам напрямую: <a href={`mailto:${TARGET_EMAIL}`}>{TARGET_EMAIL}</a>
        </p>
        <p className="request-demo__success-note">
          Менеджер свяжется с вами в течение 1 рабочего дня для подбора времени
          демонстрации.
        </p>
        <div className="request-demo__success-ctas">
          <Link to="/" className="btn-primary">
            На главную
          </Link>
          <button
            className="btn-secondary"
            onClick={() => {
              setData(INITIAL);
              setSubmitted(false);
              setErrors({});
            }}
          >
            Отправить ещё один запрос
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="request-demo">
      <header className="request-demo__header">
        <Link to="/" className="request-demo__back">
          ← На главную
        </Link>
        <h1>Запросить демонстрацию</h1>
        <p className="request-demo__subtitle">
          Заполните форму, и менеджер свяжется с вами в течение 1 рабочего дня.
          Демонстрация — бесплатно, без обязательств.
        </p>
      </header>

      <form className="request-demo__form" onSubmit={onSubmit} noValidate>
        <div className="request-demo__row">
          <label className="request-demo__field">
            <span className="request-demo__label">
              Имя <span className="request-demo__required">*</span>
            </span>
            <input
              type="text"
              className="request-demo__input"
              value={data.name}
              onChange={(e) => update("name", e.target.value)}
              required
              autoComplete="name"
            />
            {errors.name && <span className="request-demo__error">{errors.name}</span>}
          </label>

          <label className="request-demo__field">
            <span className="request-demo__label">
              Email <span className="request-demo__required">*</span>
            </span>
            <input
              type="email"
              className="request-demo__input"
              value={data.email}
              onChange={(e) => update("email", e.target.value)}
              required
              autoComplete="email"
            />
            {errors.email && <span className="request-demo__error">{errors.email}</span>}
          </label>
        </div>

        <div className="request-demo__row">
          <label className="request-demo__field">
            <span className="request-demo__label">
              Компания <span className="request-demo__required">*</span>
            </span>
            <input
              type="text"
              className="request-demo__input"
              value={data.company}
              onChange={(e) => update("company", e.target.value)}
              required
              autoComplete="organization"
            />
            {errors.company && <span className="request-demo__error">{errors.company}</span>}
          </label>

          <label className="request-demo__field">
            <span className="request-demo__label">Роль / должность</span>
            <input
              type="text"
              className="request-demo__input"
              value={data.role}
              onChange={(e) => update("role", e.target.value)}
              placeholder="Например: ИТ-директор, HSE-руководитель"
              autoComplete="organization-title"
            />
          </label>

          <label className="request-demo__field">
            <span className="request-demo__label">
              Телефон <span style={{ color: "#8693b0", fontWeight: 400 }}>(опционально, для callback)</span>
            </span>
            <input
              type="tel"
              className="request-demo__input"
              value={data.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+7 495 123-45-67"
              autoComplete="tel"
            />
          </label>
        </div>

        <fieldset className="request-demo__products">
          <legend className="request-demo__label">
            Интересующие продукты (можно несколько)
          </legend>
          <div className="request-demo__products-grid">
            {PRODUCTS_OPTIONS.map((p) => (
              <label key={p.slug} className="request-demo__product-option">
                <input
                  type="checkbox"
                  checked={data.selectedProducts.includes(p.slug)}
                  onChange={() => toggleProduct(p.slug)}
                />
                <span>{p.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="request-demo__field">
          <span className="request-demo__label">Дополнительная информация</span>
          <textarea
            className="request-demo__textarea"
            value={data.message}
            onChange={(e) => update("message", e.target.value)}
            rows={4}
            placeholder="Расскажите о вашей задаче или конкретном кейсе использования"
          />
        </label>

        {/* Cycle 9 STORY-805: honeypot anti-spam field (hidden from real users) */}
        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
        >
          <label>
            Не заполняйте это поле
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </label>
        </div>

        <div className="request-demo__actions">
          <button
            type="submit"
            className="btn-primary request-demo__submit"
            disabled={submitting}
          >
            {submitting ? "Отправляем…" : "Отправить запрос →"}
          </button>
          {submitError && (
            <p className="request-demo__api-error" role="alert">
              {submitError}
            </p>
          )}
          <p className="request-demo__privacy">
            Отправляя форму, вы соглашаетесь на обработку персональных данных
            для обратной связи. Мы не передаём данные третьим лицам.
          </p>
        </div>
      </form>
    </section>
  );
}
